// Watabaga staff FRIDGE overlay — a building's shared store inventory (capacity in kg; every item is
// 1kg). Left: the fridge (staff-shared); right: your bag. Click a bag item to STOCK it (stocking a
// resellable menu item pays you), or a fridge item to TAKE it back out. Staff-only; the server gates
// access and is authoritative — this just mirrors fridgeStore + inventoryStore and sends intents.

import { INVENTORY_COLS, FRIDGE_CAPACITY_KG, ITEM_NAMES, buildingName, isBuildingId, type InventorySlot } from "@ghostland/shared";
import { fridgeStore } from "../items/fridgeStore.js";
import { foodMenuStore } from "../items/foodMenuStore.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { itemThumb } from "../items/itemThumb.js";

export interface FridgeActions {
  request: () => void;
  store: (slotIndex: number) => void;            // bag → fridge
  take: (slotIndex: number) => void;             // fridge → bag
  setMenu: (items: string[]) => void;            // owner: set which items the NPC offers
  setPrice: (itemId: string, price: number) => void; // owner: set an item's NPC price (≥ cost)
  setCost: (itemId: string, cost: number) => void;   // owner: set an item's per-task wage cost
  setMax: (itemId: string, max: number) => void;     // owner: cap how many the chest holds
  collect: (amount: number) => void;             // owner: withdraw from the register
  deposit: (amount: number) => void;             // owner: deposit cash into the register
}
let actions: FridgeActions | null = null;
export function setFridgeActions(a: FridgeActions): void { actions = a; }

const STYLE_ID = "ghostland-fridge-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-frdg-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center; background: rgba(0,0,0,.7); color: #e8e8e8;
}
#gl-frdg-overlay, #gl-frdg-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }
#gl-frdg-overlay.open { display: flex; }
#gl-frdg-card { width: 560px; padding: 20px 18px 16px; background: #000; border: 2px solid #f4f4f4; image-rendering: pixelated; }
#gl-frdg-card h1 { margin: 0 0 2px; font-size: 11px; letter-spacing: 2px; color: #f4f4f4; }
#gl-frdg-card .hint { font-size: 7px; color: #7a7a7a; letter-spacing: 1px; margin-bottom: 14px; }
.gl-frdg-cols { display: flex; gap: 18px; }
.gl-frdg-col { flex: 1; }
.gl-frdg-col h2 { margin: 0 0 8px; font-size: 8px; letter-spacing: 1px; color: #b0b0b0; }
.gl-frdg-grid { display: grid; gap: 4px; }
.gl-frdg-cell {
  width: 100%; aspect-ratio: 1; background: #0c0c0c; border: 1px solid #2a2a2a;
  display: flex; align-items: flex-end; justify-content: flex-end; position: relative; cursor: pointer;
}
.gl-frdg-cell.empty { cursor: default; }
.gl-frdg-cell.locked { cursor: default; }
.gl-frdg-cell:not(.empty):not(.locked):hover { border-color: #f4f4f4; }
.gl-frdg-cell .lk { position: absolute; top: 1px; left: 1px; font-size: 8px; line-height: 1; opacity: .85; }
.gl-frdg-cell canvas { position: absolute; inset: 0; width: 100%; height: 100%; image-rendering: pixelated; }
.gl-frdg-cell .q { position: relative; font-size: 8px; color: #f4f4f4; padding: 0 2px 1px; text-shadow: 1px 1px 0 #000; }
#gl-frdg-bar { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; padding-top: 12px; border-top: 1px solid #1c1c1c; }
#gl-frdg-fill { font-size: 8px; color: #8a8a8a; letter-spacing: 1px; }
#gl-frdg-pay { font-size: 8px; color: #7fd08a; letter-spacing: 1px; }
#gl-frdg-close { margin-top: 10px; width: 100%; padding: 7px 0; background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; cursor: pointer; font-size: 8px; letter-spacing: 1px; }
#gl-frdg-close:hover { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
#gl-frdg-menu { margin-top: 14px; padding-top: 12px; border-top: 1px solid #1c1c1c; }
#gl-frdg-menu h3 { margin: 12px 0 8px; font-size: 8px; letter-spacing: 1px; color: #b0b0b0; }
#gl-frdg-reg { display: flex; align-items: center; gap: 8px; font-size: 9px; color: #d8d8d8; }
#gl-frdg-reg .bal { color: #8fe6a8; }
#gl-frdg-reg .sp { margin-left: auto; }
.gl-frdg-rbtn { padding: 6px 10px; background: #0c2a14; border: 1px solid #2e7d46; color: #8fe6a8; cursor: pointer; font-size: 8px; letter-spacing: 1px; }
.gl-frdg-rbtn:hover { background: #2e7d46; color: #000; }
.gl-frdg-mrow { display: flex; align-items: center; gap: 8px; padding: 5px 6px; font-size: 9px; color: #d8d8d8; }
.gl-frdg-mbox { width: 12px; height: 12px; border: 1px solid #6a6a6a; display: inline-block; text-align: center; line-height: 11px; color: #000; cursor: pointer; }
.gl-frdg-mbox.on { background: #f4f4f4; }
.gl-frdg-mrow .nm { flex: 1; }
.gl-frdg-mrow .pin, .gl-frdg-mrow .cin, .gl-frdg-mrow .xin { width: 48px; padding: 4px 5px; background: #000; border: 1px solid #6a6a6a; color: #f4f4f4; font: inherit; font-size: 9px; text-align: right; }
.gl-frdg-mrow .stk { min-width: 14px; text-align: center; color: #8fe6a8; font-size: 9px; }
.gl-frdg-mrow .ct { color: #7a7a7a; font-size: 8px; }
`;

let overlay: HTMLDivElement | null = null;
let fridgeGrid: HTMLDivElement | null = null;
let bagGrid: HTMLDivElement | null = null;
let titleEl: HTMLHeadingElement | null = null;
let colHeadEl: HTMLHeadingElement | null = null;
let fillEl: HTMLDivElement | null = null;
let menuBox: HTMLDivElement | null = null;
let menuRows: HTMLDivElement | null = null;
let regBox: HTMLDivElement | null = null;
let unsub: Array<() => void> = [];

/** Total units (kg) currently in the fridge. */
function usedKg(slots: InventorySlot[]): number {
  return slots.reduce((n, s) => n + s.quantity, 0);
}

function cell(slot: InventorySlot | undefined, onClick: (() => void) | null, locked = false): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "gl-frdg-cell" + (slot ? "" : " empty") + (locked ? " locked" : "");
  if (slot) {
    el.appendChild(itemThumb(slot.itemId));
    if (slot.quantity > 1) { const q = document.createElement("span"); q.className = "q"; q.textContent = String(slot.quantity); el.appendChild(q); }
    if (locked) { const lk = document.createElement("span"); lk.className = "lk"; lk.textContent = "🔒"; el.appendChild(lk); }
    if (onClick && !locked) el.addEventListener("click", onClick);
  }
  return el;
}

/** Render the fridge grid: a cell per occupied stack, padded to a tidy 5-wide grid that matches the bag
 *  so both columns are the same size and nothing scrolls (food items stack, so stacks stay few). */
function renderFridge(grid: HTMLDivElement, slots: InventorySlot[]): void {
  grid.style.gridTemplateColumns = `repeat(${INVENTORY_COLS}, 1fr)`;
  const sorted = [...slots].sort((a, b) => a.slotIndex - b.slotIndex);
  // Paid sell-stock (any item whose stocking pays a wage) is locked — only customers can take it. The
  // OWNER is exempt: they can pull locked stock back into their bag.
  const isOwner = foodMenuStore.get().owner;
  const sellStock = new Set(foodMenuStore.get().items.filter((i) => i.cost > 0).map((i) => i.itemId));
  const cells = sorted.map((s) => { const locked = sellStock.has(s.itemId) && !isOwner; return cell(s, locked ? null : () => actions?.take(s.slotIndex), locked); });
  // pad to a full 20-cell grid (4 rows of 5) — matches the bag so the two columns line up.
  for (let i = cells.length; i < 20; i++) cells.push(cell(undefined, null));
  grid.replaceChildren(...cells);
}

function renderBag(grid: HTMLDivElement, slots: InventorySlot[]): void {
  grid.style.gridTemplateColumns = `repeat(${INVENTORY_COLS}, 1fr)`;
  const by = new Map(slots.map((s) => [s.slotIndex, s]));
  const cells: HTMLDivElement[] = [];
  for (let i = 0; i < 20; i++) {
    const s = by.get(i);
    cells.push(cell(s, s ? () => actions?.store(i) : null));
  }
  grid.replaceChildren(...cells);
}

function refresh(): void {
  const fr = fridgeStore.get();
  const bag = inventoryStore.get();
  const used = usedKg(fr.slots);
  const cap = fr.capacity || FRIDGE_CAPACITY_KG;
  const bid = foodMenuStore.get().buildingId || fr.buildingId;
  const name = isBuildingId(bid) ? buildingName(bid).toUpperCase() : "STORE";
  if (titleEl) titleEl.textContent = `${name} STORE  ${used}/${cap}kg`;
  if (colHeadEl) colHeadEl.textContent = `${name} CHEST`;
  if (fridgeGrid) renderFridge(fridgeGrid, fr.slots);
  if (bagGrid) renderBag(bagGrid, bag);
  if (fillEl) fillEl.textContent = `${used} / ${cap} kg stored`;
  renderMenuEditor();
}

/** Owner-only: the building register (deposit/collect) + a per-item editor (toggle on the menu + set
 *  the NPC price, floored at the item's cost). Lives in the fridge ("store inventory") panel. */
function renderMenuEditor(): void {
  if (!menuBox || !menuRows || !regBox) return;
  const fm = foodMenuStore.get();
  const fr = fridgeStore.get();
  // Only show the editor to the owner of THIS building's fridge.
  if (!fm.owner || (fr.buildingId && fm.buildingId && fr.buildingId !== fm.buildingId)) { menuBox.style.display = "none"; return; }
  menuBox.style.display = "block";

  // Register row: balance + Deposit / Collect.
  regBox.innerHTML = `<span>Register:</span><span class="bal">$${fm.funds.toLocaleString()}</span>
    <span class="sp"></span>
    <button class="gl-frdg-rbtn" id="gl-frdg-dep">Deposit</button>
    <button class="gl-frdg-rbtn" id="gl-frdg-col">Collect</button>`;
  regBox.querySelector("#gl-frdg-dep")!.addEventListener("click", () => {
    const raw = window.prompt("Deposit how much cash into the register? (funds wages)");
    if (raw === null) return;
    const amt = Math.max(0, Math.floor(Number(raw)));
    if (amt > 0) actions?.deposit(amt);
  });
  regBox.querySelector("#gl-frdg-col")!.addEventListener("click", () => {
    if (fm.funds <= 0) return;
    const raw = window.prompt(`Collect how much from the register? (max $${fm.funds})`, String(fm.funds));
    if (raw === null) return;
    const amt = Math.max(0, Math.floor(Number(raw)));
    if (amt > 0) actions?.collect(amt);
  });

  // Threads is AUTO-stocked: the active store = whatever's in the chest. Only stocked items are listed
  // (for the owner to set wage/price/quantity). Restaurants keep the tick-to-sell catalogue.
  const stockDriven = fm.buildingId === "threads";
  const rows = stockDriven ? fm.items.filter((i) => i.stock > 0) : fm.items;
  const h3 = menuBox.querySelector("#gl-frdg-menu-h3");
  if (h3) h3.textContent = stockDriven ? "STOCKED ITEMS — SET WAGE · PRICE · QTY" : "MENU — TICK TO SELL · SET PRICE · QTY (MIN = COST)";

  // Per-item rows: (tick to sell, unless stock-driven) + an editable wage cost + price (≥ cost) + chest max.
  menuRows.innerHTML = "";
  if (stockDriven && rows.length === 0) {
    const empty = document.createElement("div"); empty.className = "gl-frdg-mrow"; empty.style.color = "#7a7a7a";
    empty.textContent = "Nothing stocked yet — add clothes to the chest to put them on sale.";
    menuRows.appendChild(empty);
  }
  for (const it of rows) {
    const name = ITEM_NAMES[it.itemId as keyof typeof ITEM_NAMES] ?? it.itemId;
    const row = document.createElement("div"); row.className = "gl-frdg-mrow";
    const lead = stockDriven
      ? `<span class="stk" title="in stock">${it.stock}</span>`
      : `<span class="gl-frdg-mbox${it.onMenu ? " on" : ""}">${it.onMenu ? "✓" : ""}</span>`;
    row.innerHTML = lead +
      `<span class="nm">${name}</span>` +
      `<span class="ct">wage $</span><input class="cin" type="number" min="0" value="${it.cost}" />` +
      `<span class="ct">price $</span><input class="pin" type="number" min="${it.cost}" value="${it.price}" />` +
      `<span class="ct">max</span><input class="xin" type="number" min="1" value="${it.max}" />`;
    if (!stockDriven) row.querySelector(".gl-frdg-mbox")!.addEventListener("click", () => {
      const next = new Set(fm.items.filter((i) => i.onMenu).map((i) => i.itemId));
      if (next.has(it.itemId)) next.delete(it.itemId); else next.add(it.itemId);
      actions?.setMenu([...next]);
    });
    const cin = row.querySelector<HTMLInputElement>(".cin")!;
    const applyCost = (): void => {
      const c = Math.max(0, Math.floor(Number(cin.value) || 0));
      cin.value = String(c);
      if (c !== it.cost) actions?.setCost(it.itemId, c);
    };
    cin.addEventListener("change", applyCost);
    cin.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") applyCost(); });
    const pin = row.querySelector<HTMLInputElement>(".pin")!;
    const applyPrice = (): void => {
      const p = Math.max(it.cost, Math.floor(Number(pin.value) || it.cost));
      pin.value = String(p);
      if (p !== it.price) actions?.setPrice(it.itemId, p);
    };
    pin.addEventListener("change", applyPrice);
    pin.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") applyPrice(); });
    const xin = row.querySelector<HTMLInputElement>(".xin")!;
    const applyMax = (): void => {
      const m = Math.max(1, Math.floor(Number(xin.value) || it.max));
      xin.value = String(m);
      if (m !== it.max) actions?.setMax(it.itemId, m);
    };
    xin.addEventListener("change", applyMax);
    xin.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") applyMax(); });
    menuRows.appendChild(row);
  }
}

function ensure(): void {
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-frdg-overlay";
  overlay.innerHTML = `
    <div id="gl-frdg-card">
      <h1 id="gl-frdg-title">FRIDGE</h1>
      <div class="hint">staff store — stock menu items to get paid; take stock back out anytime</div>
      <div class="gl-frdg-cols">
        <div class="gl-frdg-col"><h2 id="gl-frdg-colhead">STORE CHEST</h2><div class="gl-frdg-grid" id="gl-frdg-fridge"></div></div>
        <div class="gl-frdg-col"><h2>YOUR BAG</h2><div class="gl-frdg-grid" id="gl-frdg-bag"></div></div>
      </div>
      <div id="gl-frdg-bar">
        <div id="gl-frdg-fill">0 / ${FRIDGE_CAPACITY_KG} kg stored</div>
        <div id="gl-frdg-pay">stock a menu item → +$30</div>
      </div>
      <div id="gl-frdg-menu" style="display:none">
        <div id="gl-frdg-reg"></div>
        <h3 id="gl-frdg-menu-h3">MENU — TICK TO SELL · SET PRICE (MIN = COST)</h3>
        <div id="gl-frdg-menu-rows"></div>
      </div>
      <button id="gl-frdg-close">Close (Esc)</button>
    </div>`;
  document.body.appendChild(overlay);
  fridgeGrid = overlay.querySelector("#gl-frdg-fridge");
  bagGrid = overlay.querySelector("#gl-frdg-bag");
  titleEl = overlay.querySelector("#gl-frdg-title");
  colHeadEl = overlay.querySelector("#gl-frdg-colhead");
  fillEl = overlay.querySelector("#gl-frdg-fill");
  menuBox = overlay.querySelector("#gl-frdg-menu");
  menuRows = overlay.querySelector("#gl-frdg-menu-rows");
  regBox = overlay.querySelector("#gl-frdg-reg");
  overlay.querySelector("#gl-frdg-close")!.addEventListener("click", closeFridge);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeFridge(); });
}

export function openFridge(): void {
  ensure();
  overlay!.classList.add("open");
  unsub = [fridgeStore.subscribe(refresh), inventoryStore.subscribe(refresh), foodMenuStore.subscribe(refresh)];
  actions?.request();
}

export function closeFridge(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
}

export function isFridgeOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
